import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bot, UserRound, Sparkles } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { api, fmt, supabase } from "../client";
import { Panel, Pill, Empty } from "../ui";

const ROLE_STYLE = {
  user: "bg-white/[0.04]",
  ai: "ml-auto border border-[var(--s-line)] bg-white/[0.02]",
  staff: "ml-auto border border-[rgba(242,179,61,0.35)] bg-[rgba(242,179,61,0.12)]",
};

const Console = ({ id, onChanged }) => {
  const [c, setC] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const suggest = async () => {
    setDrafting(true);
    try { const r = await api(`/staff/conversations/${id}/suggest`, { method: "POST" }); setText(r.draft); }
    catch (e) { toast.error(e.message); } finally { setDrafting(false); }
  };
  const load = useCallback(() => { api(`/staff/conversations/${id}`).then(setC).catch((e) => toast.error(e.message)); }, [id]);
  useEffect(() => {
    setC(null);
    load();
    const ch = supabase.channel(`support-${id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "support_messages", filter: `conversation_id=eq.${id}` }, load)
      .on("postgres_changes", { event: "UPDATE", schema: "public", table: "support_conversations", filter: `id=eq.${id}` }, load)
      .subscribe();
    const poll = setInterval(load, 6000);
    return () => { supabase.removeChannel(ch); clearInterval(poll); };
  }, [id, load]);
  const post = async (path, body, msg) => {
    setBusy(true);
    try { await api(`/staff/conversations/${id}/${path}`, { method: "POST", body }); toast.success(msg); load(); onChanged(); return true; }
    catch (e) { toast.error(e.message); return false; } finally { setBusy(false); }
  };
  if (!c) return <p className="p-6 text-sm text-[var(--s-dim)]">Loading…</p>;
  return (
    <div className="flex h-full flex-col" data-testid="chat-console">
      <div className="border-b border-[var(--s-line)] p-5">
        <div className="flex flex-wrap items-center gap-2">
          {c.requires_human && <Pill tone="warn" data-testid="chat-needs-human">Needs Human Action</Pill>}
          <Pill tone={c.ai_handled ? "ok" : "dim"} data-testid="chat-handler">{c.ai_handled ? "AI answering" : "Staff answering"}</Pill>
          {c.escalation_reason && <Pill>{c.escalation_reason}</Pill>}
        </div>
        <p className="mt-3 text-sm" data-testid="chat-user">{c.user_email || c.user_id}</p>
        {c.ai_summary && <p className="mt-2 rounded-lg bg-white/[0.03] p-3 text-xs text-[var(--s-dim)]" data-testid="chat-ai-summary">AI summary: {c.ai_summary}</p>}
        {(!c.ai_handled || c.requires_human) && <Button size="sm" variant="outline" className="s-ghost mt-3" disabled={busy} onClick={() => post("handback", undefined, "Handed back to AI")} data-testid="chat-handback-btn"><Bot size={14} className="mr-1" />Hand back to AI</Button>}
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-5" data-testid="chat-thread">
        {c.messages.length === 0 && <p className="text-sm text-[var(--s-dim)]">No messages yet.</p>}
        {c.messages.map((m) => (
          <div key={m.id} className={`max-w-[85%] rounded-lg p-3 text-sm ${ROLE_STYLE[m.sender_role] || ROLE_STYLE.user}`} data-testid={`chat-msg-${m.sender_role}`}>
            <p className="whitespace-pre-wrap">{m.content}</p>
            <p className="s-mono mt-1 text-[10px] uppercase text-[var(--s-dim)]">{m.sender_role} · {fmt(m.created_at)}</p>
          </div>
        ))}
      </div>
      <form className="border-t border-[var(--s-line)] p-4" onSubmit={async (e) => { e.preventDefault(); if (text.trim() && await post("reply", { text }, "Reply sent")) setText(""); }}>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={c.ai_handled ? "Replying takes over from the AI…" : "Write a reply…"} className="s-input" data-testid="chat-reply-input" />
        <div className="mt-2 flex flex-wrap gap-2">
          <Button type="submit" disabled={busy} className="s-btn" data-testid="chat-reply-submit"><UserRound size={14} className="mr-1" />Send reply</Button>
          <Button type="button" variant="outline" disabled={drafting || c.messages.length === 0} onClick={suggest} className="s-ghost" data-testid="chat-suggest-btn"><Sparkles size={14} className="mr-1" />{drafting ? "Drafting…" : "Suggest reply"}</Button>
        </div>
      </form>
    </div>
  );
};

const Group = ({ title, rows, sel, setSel, testid }) => (
  <div data-testid={testid}>
    <p className="s-mono px-4 pb-2 pt-4 text-[11px] uppercase tracking-wider text-[var(--s-dim)]">{title} ({rows.length})</p>
    {rows.length === 0 ? <p className="px-4 pb-3 text-xs text-[var(--s-dim)]">None</p> : rows.map((c) => (
      <button key={c.id} onClick={() => setSel(c.id)} data-testid={`chat-row-${c.id}`}
        className={`block w-full px-4 py-3 text-left transition-colors hover:bg-white/[0.03] ${sel === c.id ? "border-l-2 border-[var(--s-accent)] bg-white/[0.03]" : ""}`}>
        <div className="flex items-center gap-2">
          <p className="flex-1 truncate text-sm">{c.user_email || c.user_id}</p>
          {c.requires_human && <Pill tone="warn">Needs Human Action</Pill>}
        </div>
        <p className="s-mono text-[10px] text-[var(--s-dim)]">{c.escalation_reason || (c.ai_handled ? "AI" : "staff")} · {fmt(c.updated_at)}</p>
      </button>
    ))}
  </div>
);

export default function LiveChats() {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => { api("/staff/conversations").then(setRows).catch((e) => toast.error(e.message)); }, []);
  useEffect(() => {
    load();
    const ch = supabase.channel("support-conversations")
      .on("postgres_changes", { event: "*", schema: "public", table: "support_conversations" }, load)
      .subscribe();
    const poll = setInterval(load, 10000);
    return () => { supabase.removeChannel(ch); clearInterval(poll); };
  }, [load]);
  const human = (rows || []).filter((c) => c.requires_human);
  const staffed = (rows || []).filter((c) => !c.requires_human && !c.ai_handled);
  const ai = (rows || []).filter((c) => !c.requires_human && c.ai_handled);
  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(260px,360px)_1fr]">
      <Panel className="max-h-[70vh] overflow-y-auto" data-testid="chat-list">
        {rows === null ? <p className="p-5 text-sm text-[var(--s-dim)]">Loading…</p> : <>
          <Group title="Needs human action" rows={human} sel={sel} setSel={setSel} testid="chat-group-human" />
          <Group title="Staff handling" rows={staffed} sel={sel} setSel={setSel} testid="chat-group-staff" />
          <Group title="AI handled" rows={ai} sel={sel} setSel={setSel} testid="chat-group-ai" />
        </>}
      </Panel>
      <Panel className="min-h-[50vh]">{sel ? <Console key={sel} id={sel} onChanged={load} /> : <Empty>Select a conversation to open the live console.</Empty>}</Panel>
    </div>
  );
}
